/**
 * Minimal markdown → React renderer for the curriculum lesson HUD.
 *
 * Supported subset (matches our lesson .md files):
 *   #   → H1
 *   ##  → H2
 *   ### → H3
 *   - / *  → bullet list
 *   blank line → paragraph break
 *   plain text → paragraph
 *
 * NOT supported: code blocks, images, tables, inline links. Lesson files
 * don't use those today. If they ever do, swap for `react-markdown`.
 */
import { Fragment } from "react";

function renderInline(text, keyPrefix = "i") {
    // Very small subset: **bold**, _italic_, leave the rest alone.
    const parts = [];
    let buf = "";
    let i = 0;
    let k = 0;
    while (i < text.length) {
        if (text[i] === "*" && text[i + 1] === "*") {
            if (buf) parts.push(buf);
            const end = text.indexOf("**", i + 2);
            if (end === -1) { buf += "**"; i += 2; continue; }
            parts.push(<strong key={`${keyPrefix}-b-${k++}`} className="text-tca-ink">{text.slice(i + 2, end)}</strong>);
            i = end + 2;
            buf = "";
        } else if (text[i] === "_" && text[i + 1] && text[i + 1] !== " ") {
            const end = text.indexOf("_", i + 1);
            if (end === -1) { buf += "_"; i += 1; continue; }
            if (buf) parts.push(buf);
            parts.push(<em key={`${keyPrefix}-em-${k++}`} className="text-tca-cyan-hi">{text.slice(i + 1, end)}</em>);
            i = end + 1;
            buf = "";
        } else {
            buf += text[i];
            i++;
        }
    }
    if (buf) parts.push(buf);
    return parts;
}

export default function LessonMarkdown({ source }) {
    if (!source) return null;
    const lines = source.split("\n");
    const blocks = [];
    let listBuf = [];
    let paraBuf = [];

    const flushList = (key) => {
        if (!listBuf.length) return;
        blocks.push(
            <ul key={`ul-${key}`} className="list-disc pl-5 space-y-1 my-3 text-tca-ink/90 text-[15px] leading-[1.7]">
                {listBuf.map((item, i) => (
                    <li key={i}>{renderInline(item, `li-${key}-${i}`)}</li>
                ))}
            </ul>
        );
        listBuf = [];
    };
    const flushPara = (key) => {
        if (!paraBuf.length) return;
        const joined = paraBuf.join(" ");
        blocks.push(
            <p key={`p-${key}`} className="my-3 text-tca-ink/90 text-[15px] leading-[1.7]">
                {renderInline(joined, `p-${key}`)}
            </p>
        );
        paraBuf = [];
    };

    lines.forEach((raw, idx) => {
        const line = raw.trim();
        if (!line) {
            flushList(idx);
            flushPara(idx);
            return;
        }
        if (line.startsWith("### ")) {
            flushList(idx); flushPara(idx);
            blocks.push(
                <h3 key={`h3-${idx}`} className="mt-6 mb-2 font-mono-tight text-[11px] uppercase tracking-[0.28em] text-tca-cyan/85">
                    {line.slice(4)}
                </h3>
            );
        } else if (line.startsWith("## ")) {
            flushList(idx); flushPara(idx);
            blocks.push(
                <h2 key={`h2-${idx}`} className="mt-7 mb-3 font-mono-tight text-[10px] uppercase tracking-[0.32em] text-tca-cyan/85">
                    // {line.slice(3)}
                </h2>
            );
        } else if (line.startsWith("# ")) {
            flushList(idx); flushPara(idx);
            blocks.push(
                <h1 key={`h1-${idx}`} className="mt-4 mb-4 font-display text-2xl text-tca-ink">
                    {line.slice(2)}
                </h1>
            );
        } else if (line.startsWith("- ") || line.startsWith("* ")) {
            flushPara(idx);
            listBuf.push(line.slice(2));
        } else if (line.startsWith("> ")) {
            flushList(idx); flushPara(idx);
            blocks.push(
                <blockquote key={`bq-${idx}`} className="my-4 pl-4 border-l-2 border-tca-cyan/55 text-tca-ink-muted italic text-[15px]">
                    {renderInline(line.slice(2), `bq-${idx}`)}
                </blockquote>
            );
        } else {
            flushList(idx);
            paraBuf.push(line);
        }
    });
    flushList("end");
    flushPara("end");

    return <Fragment>{blocks}</Fragment>;
}
